<!-- 
Panel showing tasks that can be run at the levy collection level (i.e. in bulk against multiple providers).

Not all provider tasks can be run from here. This is because:
- some provider tasks require additional input (e.g. adjusting levy calculations)
- other provider tasks are considered too unsafe to allow running in bulk (e.g. reversing invoices)
- other provider tasks don't make sense to run in bulk (e.g. set as favourite, pausing)
-->
<template>
  <div class="pb-3 ml-4 mr-4">
    <our-loading-indicator v-if="state.loading"/>
    <v-scale-transition>
      <div v-if="!state.loading">
        <p v-if="noApplicableTasks && !search" class="mt-4 ml-4 grey--text text--darken-1">
          Currently there are no applicable tasks.
        </p>
        <p v-if="applicableTasks.length === 0 && inapplicableTasks.length === 0 && optionalTasks.length === 0" class="mt-4 ml-4 grey--text text--darken-1">
          No {{ search ? "matching" : "" }} provider tasks {{ search ? "found" : "available" }}.
        </p>
        <div v-for="task in applicableTasks" :key="task.type">
          <provider-task-card :task="task" @run="runTask" />
        </div>
        <div v-if="applicableTasks.length && optionalTasks.length" class="text-xs-center">
          <v-icon class="grey--text">more_horiz</v-icon>
        </div>
        <h2 v-if="optionalTasks.length" class="title font-weight-regular mb-3 mt-3 grey--text text--darken-1">Optional provider tasks</h2>
        <div v-for="task in optionalTasks" :key="task.type">
          <provider-task-card :task="task" muted @run="runTask" />
        </div>
        <v-btn 
          v-if="!search"
          flat small class="mt-2 ml-3"
          color="primary"
          @click="showNotApplicable=!showNotApplicable"
        >
          Show unavailable tasks ({{ inapplicableTasks.length }})
          <our-expand-icon :expanded="showNotApplicable"/>
        </v-btn>
        <div v-if="showNotApplicable || search" class="ml-4 mr-4">
          <div v-for="task in inapplicableTasks" :key="task.type">
            <div class="mt-4 mb-5">
              <h3 class="grey--text title mb-3 mt-4">
                {{ lookups.providerTaskTypeDescriptions[task.type] }}
              </h3>
              <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
              <p class="grey--text" v-html="task.description"/>
              <task-applicability :task="task" />
            </div>
          </div>
        </div>
      </div>
    </v-scale-transition>
    <run-all-provider-tasks-dialog 
      v-model="showRunTaskDialog" 
      :levy-collection-id="levyCollectionId" 
      :task="taskToRun"
    />
  </div>
</template>

<script>
import * as action from "@/store/levy-collections/action-types";
import lookupMixins from "@/mixins/lookup.mixins";
import TaskApplicability from "./TaskApplicability.vue";
import ProviderTaskCard from "./ProviderTaskCard.vue";
import RunAllProviderTasksDialog from "@/components/levy-collections/RunAllProviderTasksDialog.vue";
import taskMixins from "@/mixins/task.mixins";

export default {
  components: { TaskApplicability, ProviderTaskCard, RunAllProviderTasksDialog },
  mixins: [lookupMixins, taskMixins],
  props: {
    levyCollectionId: {
      required: true,
      type: Number
    },
    search: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      showNotApplicable: false,
      showRunTaskDialog: false,
      taskToRun: null
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.providerTasks;
    },
    loaded() {
      return this.state.loaded;
    },
    tasks() {
      return this.state.data;
    },
    applicableTasks() {
      return this.searchTasks(
        this.tasks.filter(t => t.isApplicable && !this.isOptional(t.type)),
        this.lookups.providerTaskTypeDescriptions,
        this.search
      );
    },
    optionalTasks() {
      return this.searchTasks(
        this.tasks.filter(t => t.isApplicable && this.isOptional(t.type)),
        this.lookups.providerTaskTypeDescriptions,
        this.search
      );
    },
    inapplicableTasks() {
      return this.searchTasks(
        this.tasks.filter(t => !t.isApplicable),
        this.lookups.providerTaskTypeDescriptions,
        this.search
      );
    },
    noApplicableTasks() {
      return this.applicableTasks.length === 0;
    }
  },
  watch: {
    levyCollectionId() {
      this.refresh();
    },
    loaded() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      if ((!this.loaded && !this.state.loading) || this.state.levyCollectionId != this.levyCollectionId) {
        this.$store.dispatch(action.GET_PROVIDER_TASKS, this.levyCollectionId);
      }
    },
    isOptional(taskType) {
      return this.doesLookupHaveAttribute(taskType, "OptionalNotice", this.lookups.providerTaskTypes);
    },
    runTask(task) {
      this.taskToRun = task;
      this.showRunTaskDialog = true;
    }
  }
};
</script>
